import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Award,
  Users,
  Clock,
  Star,
  Loader2,
  Shield,
  ThumbsUp,
  Zap,
  CheckCircle,
  Phone,
  Calendar,
  Settings,
  Quote
} from 'lucide-react';
import { api } from '../services/api';
import toast from 'react-hot-toast';

const HomePage = () => {
  const [banners, setBanners] = useState([]);
  const [services, setServices] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    loadHomepageContent();
  }, []);

  const loadHomepageContent = async () => {
    try {
      setLoading(true);
      const [bannersRes, servicesRes, statsRes] = await Promise.all([
        api.get('/cms/banners/homepage/public'),
        api.get('/cms/services/homepage'),
        api.get('/cms/stats/homepage')
      ]);

      const bannersData = Array.isArray(bannersRes.data) ? bannersRes.data : (bannersRes.data?.data || []);
      const servicesData = Array.isArray(servicesRes.data) ? servicesRes.data : (servicesRes.data?.data || []);
      const statsData = Array.isArray(statsRes.data) ? statsRes.data : (statsRes.data?.data || []);

      // Filter active CMS banners and sort by order
      const activeBanners = bannersData
        .filter(b => b && b.isActive)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      // Store CMS banners separately (not replacing the default banner)
      setBanners(activeBanners);
      setServices(servicesData.filter(s => s && s.isActive).slice(0, 6));
      setStats(statsData.filter(s => s && s.isActive).slice(0, 4));
    } catch (error) {
      console.error('Failed to load homepage content:', error);
      // Load empty arrays on error
      setBanners([]);
      setServices([]);
      setStats(getDefaultStats());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultBanner = () => [
    {
      _id: 'default',
      title: 'Your Trusted Maintenance Partner',
      subtitle: 'SwiftFixPro',
      description: 'Professional property maintenance services across Singapore',
      buttonText: 'Explore Services',
      buttonLink: '/services',
      backgroundType: 'gradient',
      gradientColors: ['#ff6b35', '#f7931e'],
      textColor: '#ffffff',
      isActive: true
    }
  ];

  const getDefaultStats = () => [
    { _id: '1', number: '500+', label: 'Happy Customers', icon: 'Users', isActive: true },
    { _id: '2', number: '50+', label: 'Expert Technicians', icon: 'Award', isActive: true },
    { _id: '3', number: '24/7', label: 'Support Available', icon: 'Clock', isActive: true },
    { _id: '4', number: '4.9', label: 'Average Rating', icon: 'Star', isActive: true }
  ];

  const getIconComponent = (iconName) => {
    const icons = { Users, Award, Clock, Star };
    return icons[iconName] || Star;
  };

  // Auto-play banner carousel
  useEffect(() => {
    if (isAutoPlaying && banners.length > 1) {
      const interval = setInterval(() => {
        setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
      }, banners[currentBannerIndex]?.displayDuration || 5000);
      return () => clearInterval(interval);
    }
  }, [currentBannerIndex, banners, isAutoPlaying]);

  const nextBanner = () => {
    setIsAutoPlaying(false);
    setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
  };

  const prevBanner = () => {
    setIsAutoPlaying(false);
    setCurrentBannerIndex((prev) => (prev - 1 + banners.length) % banners.length);
  };

  const goToBanner = (index) => {
    setIsAutoPlaying(false);
    setCurrentBannerIndex(index);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-12 h-12 text-orange-600 animate-spin" />
      </div>
    );
  }

  const currentBanner = banners.length > 0 ? banners[currentBannerIndex] : null;

  return (
    <div className="min-h-screen">
      {/* Default Hero Banner - Always Shown */}
      <section className="relative overflow-hidden">
        {/* Background with geometric patterns */}
        <div className="absolute inset-0 bg-orange-600">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700"></div>
          {/* Geometric shapes */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500 rounded-full opacity-20 transform translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-orange-700 rounded-full opacity-30 transform -translate-x-24 translate-y-24"></div>
          <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-white opacity-5 rounded-full"></div>
          <div className="absolute top-1/4 right-1/3 w-48 h-48 bg-orange-400 rounded-full opacity-10 transform rotate-45"></div>
        </div>

        <div className="relative container mx-auto px-4 py-24">
          <div className="max-w-4xl mx-auto text-center text-white">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="mb-6"
            >
              <span className="inline-block px-4 py-2 bg-orange-500 bg-opacity-30 rounded-full text-orange-100 text-sm font-medium mb-4 backdrop-blur-sm">
                SwiftFixPro
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
            >
              Your Trusted Maintenance Partner
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-xl text-orange-100 max-w-3xl mx-auto mb-10 leading-relaxed"
            >
              Professional property maintenance services across Singapore
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              <Link
                to="/services"
                className="inline-flex items-center bg-white px-8 py-4 rounded-lg font-semibold hover:bg-opacity-90 transition-all text-lg shadow-lg text-orange-600"
              >
                Explore Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CMS Dynamic Banners Section - Only show if banners exist */}
      {banners.length > 0 && (
        <section className="relative overflow-hidden min-h-[500px] flex items-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentBannerIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0"
          >
            {/* Background */}
            {currentBanner.backgroundType === 'gradient' && (
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(135deg, ${currentBanner.gradientColors?.[0] || '#ff6b35'}, ${currentBanner.gradientColors?.[1] || '#f7931e'})`
                }}
              >
                {/* Geometric patterns */}
                <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full opacity-10 transform translate-x-32 -translate-y-32"></div>
                <div className="absolute bottom-0 left-0 w-80 h-80 bg-black rounded-full opacity-10 transform -translate-x-24 translate-y-24"></div>
                <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-white opacity-5 rounded-full"></div>
              </div>
            )}
            {currentBanner.backgroundType === 'image' && currentBanner.backgroundImage && (
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${currentBanner.backgroundImage})` }}
              >
                <div className="absolute inset-0 bg-black bg-opacity-40"></div>
              </div>
            )}
            {currentBanner.backgroundType === 'video' && currentBanner.videoUrl && (
              <>
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover"
                >
                  <source src={currentBanner.videoUrl} type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-black bg-opacity-40"></div>
              </>
            )}

            {/* Content */}
            <div className="relative container mx-auto px-4 py-24 h-full flex items-center">
              <div className={`max-w-4xl ${currentBanner.position === 'left' ? 'mr-auto' : currentBanner.position === 'right' ? 'ml-auto' : 'mx-auto text-center'}`}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.2 }}
                  style={{ color: currentBanner.textColor || '#ffffff' }}
                >
                  {currentBanner.subtitle && (
                    <span className="inline-block px-4 py-2 bg-white bg-opacity-20 rounded-full text-sm font-medium mb-4 backdrop-blur-sm">
                      {currentBanner.subtitle}
                    </span>
                  )}

                  <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                    {currentBanner.title}
                  </h1>

                  {currentBanner.description && (
                    <p className="text-xl opacity-90 max-w-3xl mb-10 leading-relaxed">
                      {currentBanner.description}
                    </p>
                  )}

                  {currentBanner.buttonText && (
                    <Link
                      to={currentBanner.buttonLink || '/services'}
                      className="inline-flex items-center bg-white px-8 py-4 rounded-lg font-semibold hover:bg-opacity-90 transition-all text-lg shadow-lg"
                      style={{ color: currentBanner.gradientColors?.[0] || '#ff6b35' }}
                    >
                      {currentBanner.buttonText}
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  )}
                </motion.div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

          {/* Banner Navigation */}
          {banners.length > 1 && (
            <>
              {/* Previous/Next Buttons */}
              <button
                onClick={prevBanner}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-50 bg-black bg-opacity-50 hover:bg-opacity-70 backdrop-blur-sm p-3 rounded-full transition-all shadow-lg border-2 border-white border-opacity-30"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <button
                onClick={nextBanner}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-50 bg-black bg-opacity-50 hover:bg-opacity-70 backdrop-blur-sm p-3 rounded-full transition-all shadow-lg border-2 border-white border-opacity-30"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>

              {/* Dots Indicator */}
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex gap-2">
                {banners.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToBanner(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentBannerIndex
                        ? 'bg-white w-8'
                        : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {/* Stats Section */}
      {stats.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => {
                const Icon = getIconComponent(stat.icon);
                return (
                  <motion.div
                    key={stat._id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="text-center"
                  >
                    <div className="mb-4 flex justify-center">
                      <Icon className="w-8 h-8 text-orange-600" />
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-2">{stat.number}</div>
                    <div className="text-gray-600">{stat.label}</div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Featured Services Section */}
      {services.length > 0 && (
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Our Services
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Professional property maintenance services tailored to your needs
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={service._id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all group"
                >
                  {service.icon && (
                    <div className="mb-4 w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-600 transition-colors">
                      <span className="text-2xl group-hover:grayscale group-hover:brightness-200">
                        {service.icon}
                      </span>
                    </div>
                  )}
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {service.name}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {service.description}
                  </p>
                  {service.price && (
                    <p className="text-orange-600 font-semibold mb-4">
                      From ${service.price}
                    </p>
                  )}
                  <Link
                    to="/services"
                    className="text-orange-600 font-medium inline-flex items-center hover:gap-2 transition-all"
                  >
                    Learn More <ArrowRight className="ml-1 w-4 h-4" />
                  </Link>
                </motion.div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/services"
                className="inline-flex items-center bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
              >
                View All Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Customer Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                What Our Customers Say
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Don't just take our word for it - hear from our satisfied clients
              </p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                name: "Sarah Lim",
                role: "Homeowner",
                rating: 5,
                comment: "Excellent service! The technician was professional, punctual, and fixed my aircon issue quickly. Highly recommend SwiftFixPro!"
              },
              {
                name: "David Tan",
                role: "Property Manager",
                rating: 5,
                comment: "We've been using SwiftFixPro for all our properties. Their reliability and quality of work are outstanding. Great team!"
              },
              {
                name: "Michelle Wong",
                role: "Condo Owner",
                rating: 5,
                comment: "Fast response time and reasonable pricing. The plumbing work was done perfectly. Will definitely use their services again!"
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="mb-4">
                  <Quote className="w-8 h-8 text-orange-600 opacity-50" />
                </div>
                <div className="flex mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">
                  "{testimonial.comment}"
                </p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
              Book your service today and experience professional property maintenance
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/services"
                className="bg-white text-orange-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center"
              >
                Book Service Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-orange-600 transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
